import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { translations as defaults } from "../i18n/translations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LanguageContext = createContext(null);

function deepMerge(target, source) {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key]) && target[key] && typeof target[key] === "object" && !Array.isArray(target[key])) {
      result[key] = deepMerge(target[key], source[key]);
    } else if (source[key] !== undefined && source[key] !== "") {
      result[key] = source[key];
    }
  }
  return result;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("kyra_lang") || "fr"; } catch { return "fr"; }
  });
  const [customTranslations, setCustomTranslations] = useState(null);

  useEffect(() => {
    axios.get(`${API}/translations`).then(({ data }) => {
      if (data && Object.keys(data).length > 0) setCustomTranslations(data);
    }).catch(() => {});
  }, []);

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === "fr" ? "en" : "fr";
      try { localStorage.setItem("kyra_lang", next); } catch {}
      return next;
    });
  }, []);

  const merged = useMemo(() => {
    return customTranslations ? { fr: deepMerge(defaults.fr, customTranslations.fr || {}), en: deepMerge(defaults.en, customTranslations.en || {}) } : defaults;
  }, [customTranslations]);

  const t = useCallback((key) => {
    const keys = key.split(".");
    let val = merged[lang];
    for (const k of keys) {
      if (val && typeof val === "object" && k in val) {
        val = val[k];
      } else {
        let fb = merged["fr"];
        for (const fk of keys) { fb = fb?.[fk]; }
        return fb || key;
      }
    }
    return val || key;
  }, [lang, merged]);

  const reloadTranslations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/translations`);
      if (data && Object.keys(data).length > 0) setCustomTranslations(data);
    } catch {}
  }, []);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t, reloadTranslations, merged }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
}
